import { prop, type Prop } from '@tempots/dom'
import type { SavedDesign } from '@rh/shared/schemas/design'
import {
  convertCustomizeToDesign,
  customizeAgencyContact,
  type CustomizeData,
  type HeaderFooterSection,
} from '@rh/shared/templates/customize-to-design'
import type { TokenResolutionData } from '@rh/shared/templates/design-tokens'
import { UserService } from '@/services/user-service'
import type { ExtractedHeaderModel } from './pdf-extract/types'
import { emptyWriteBacks, type WriteBackIntents } from './profile-writeback'

export type WizardPath = 'import' | 'build'

export type WizardStep =
  | 'choose'
  // import path
  | 'upload'
  | 'bands'
  | 'review'
  // build path
  | 'shape'
  | 'info'
  // shared
  | 'preview'

export type HeaderShape = NonNullable<HeaderFooterSection['shape']>

/**
 * Profile data the wizard prefills from and resolves tokens against:
 * agency identity/contact (from Agency + the legacy customize seed) and
 * the current user's per-agency recruiter profile.
 */
export interface WizardProfile {
  agencyName: string
  agencyContact: {
    phone?: string
    url?: string
    email?: string
    linkedIn?: string
  }
  recruiter: {
    name?: string
    phone?: string
    email?: string
    linkedIn?: string
  }
}

export interface WizardPreferences {
  customize?: unknown
  headerLogoMeta?: { key: string }
  footerLogoMeta?: { key: string }
}

export interface WizardState {
  step: Prop<WizardStep>
  path: Prop<WizardPath>
  profile: Prop<WizardProfile>
  // ── build path (entrance B) form ─────────────────────────────
  /** Null until the user actually picks a layout on the shape step. */
  shape: Prop<HeaderShape | null>
  includeFooter: Prop<boolean>
  agencyName: Prop<string>
  agencyPhone: Prop<string>
  agencyUrl: Prop<string>
  agencyEmail: Prop<string>
  agencyLinkedIn: Prop<string>
  /**
   * The onboarder's own recruiter details, editable on the info step and
   * saved back to their Profile on finish. The design itself always holds
   * `{{recruiter.*}}` tokens — these values are what the tokens resolve
   * to for THIS user; teammates resolve to their own.
   */
  recruiterName: Prop<string>
  recruiterPhone: Prop<string>
  recruiterEmail: Prop<string>
  recruiterLinkedIn: Prop<string>
  brandColor: Prop<string>
  textColor: Prop<string>
  logoAssetKey: Prop<string | null>
  // ── import path (entrance A) ─────────────────────────────────
  /** The uploaded document as a PDF, ready for pdf.js. */
  pdfData: Prop<ArrayBuffer | null>
  pdfSourceName: Prop<string>
  extraction: Prop<ExtractedHeaderModel | null>
  /** Import bail-out message, shown on the choose/upload steps. */
  importError: Prop<string | null>
  // ── result ───────────────────────────────────────────────────
  /** The design the preview step shows and "Open in editor" hands off. */
  result: Prop<SavedDesign | null>
  /**
   * Profile fields the wizard promised to save, committed only when the
   * user finishes (see profile-writeback.ts). Steps replace the whole
   * object so the footer's "this will save…" note stays reactive.
   */
  writeBacks: Prop<WriteBackIntents>
}

export function createWizardState(
  agencyId: string,
  agencyName: string,
  preferences: WizardPreferences
): WizardState {
  const customize = preferences.customize as CustomizeData | undefined
  const contact = customizeAgencyContact(customize)

  const profile = prop<WizardProfile>({
    agencyName,
    agencyContact: contact,
    recruiter: {},
  })
  const recruiterName = prop('')
  const recruiterPhone = prop('')
  const recruiterEmail = prop('')
  const recruiterLinkedIn = prop('')
  // Recruiter profile arrives async; the wizard prefers live data but works
  // without it (tokens simply resolve empty in the preview). Form props are
  // only prefilled while still untouched, in case the user typed first.
  UserService.getAgencyProfile(agencyId)
    .then(res => {
      const r = res.recruiterProfile
      profile.set({
        ...profile.value,
        recruiter: {
          name: r?.name,
          phone: r?.phone,
          email: r?.email,
          linkedIn: r?.linkedIn,
        },
      })
      const prefill = (p: Prop<string>, v: string | undefined) => {
        if (!p.value.trim() && v) p.set(v)
      }
      prefill(recruiterName, r?.name)
      prefill(recruiterPhone, r?.phone)
      prefill(recruiterEmail, r?.email)
      prefill(recruiterLinkedIn, r?.linkedIn)
    })
    .catch(() => {
      /* profile prefill is best-effort */
    })

  return {
    step: prop<WizardStep>('choose'),
    path: prop<WizardPath>('build'),
    profile,
    shape: prop<HeaderShape | null>(null),
    includeFooter: prop(false),
    agencyName: prop(agencyName),
    agencyPhone: prop(contact.phone ?? ''),
    agencyUrl: prop(contact.url ?? ''),
    agencyEmail: prop(contact.email ?? ''),
    agencyLinkedIn: prop(contact.linkedIn ?? ''),
    recruiterName,
    recruiterPhone,
    recruiterEmail,
    recruiterLinkedIn,
    brandColor: prop(customize?.borderColor ?? '#cbd5e1'),
    textColor: prop(customize?.textColor ?? '#0F172A'),
    logoAssetKey: prop<string | null>(preferences.headerLogoMeta?.key ?? null),
    pdfData: prop<ArrayBuffer | null>(null),
    pdfSourceName: prop(''),
    extraction: prop<ExtractedHeaderModel | null>(null),
    importError: prop<string | null>(null),
    result: prop<SavedDesign | null>(null),
    writeBacks: prop<WriteBackIntents>(emptyWriteBacks()),
  }
}

const trimmed = (v: string): string | undefined => {
  const t = v.trim()
  return t.length > 0 ? t : undefined
}

/**
 * Builds the ephemeral CustomizeData the build path feeds to
 * convertCustomizeToDesign. Semantics mirror the converter's contract:
 * - Every non-empty contact field becomes its `{{agency.*}}` token: what
 *   the user typed is saved to the agency profile on finish (see
 *   formWriteBackIntents), so the token resolves to exactly that value
 *   and follows any later profile edits. One source of truth, no
 *   live-vs-static fork.
 * - agencyName equal to the live Agency.name is passed as undefined so
 *   the design keeps the `{{agency.name}}` token (rename-proof); an
 *   edited value is baked as written — a stylized header name shouldn't
 *   rename the agency's account.
 * - recruiter fields are always `{{recruiter.*}}` tokens (per-recruiter
 *   personalization is the wizard's whole pitch).
 * - contact fields show only when non-empty.
 *
 * Token literals survive convertCustomizeToDesign's escapeHtml (braces
 * aren't escaped) and resolve at render time from the same saved contact
 * this form writes (see buildTokenResolutionData in resumes.ts).
 */
export function buildFormCustomizeData(s: WizardState): CustomizeData {
  const agencyNameValue = s.agencyName.value.trim()
  const nameOverride =
    agencyNameValue && agencyNameValue !== s.profile.value.agencyName
      ? agencyNameValue
      : undefined
  const tokenIf = (raw: string, token: string): string | undefined =>
    trimmed(raw) ? token : undefined
  const section: HeaderFooterSection = {
    shape: s.shape.value ?? 'columns-3',
    showAgencyLogo: Boolean(s.logoAssetKey.value),
    showAgencyName: Boolean(agencyNameValue),
    agencyName: nameOverride,
    agencyDetails: {
      showAddressLine1: false,
      showAddressLine2: false,
      showAddressLine3: false,
      showPhoneNumber: Boolean(trimmed(s.agencyPhone.value)),
      phoneNumber: tokenIf(s.agencyPhone.value, '{{agency.phone}}'),
      showUrl: Boolean(trimmed(s.agencyUrl.value)),
      url: tokenIf(s.agencyUrl.value, '{{agency.url}}'),
      showEmailAddress: Boolean(trimmed(s.agencyEmail.value)),
      emailAddress: tokenIf(s.agencyEmail.value, '{{agency.email}}'),
      showLinkedIn: Boolean(trimmed(s.agencyLinkedIn.value)),
      linkedIn: tokenIf(s.agencyLinkedIn.value, '{{agency.linkedIn}}'),
    },
    // All four stay on: each line resolves per recruiter at render time
    // and empty ones collapse (resolveDesignTokens), so a teammate with a
    // LinkedIn shows it even when the onboarder has none.
    recruiterDetails: {
      showName: true,
      showPhoneNumber: true,
      showEmailAddress: true,
      showLinkedIn: true,
    },
  }
  return {
    textColor: s.textColor.value,
    borderColor: s.brandColor.value,
    includeHeader: true,
    header: section,
    includeFooter: s.includeFooter.value,
    footer: s.includeFooter.value ? { ...section } : undefined,
    includeWatermark: false,
  }
}

/** Builds the entrance-B design from the current form state. */
export function buildFormDesign(s: WizardState): SavedDesign {
  return convertCustomizeToDesign(buildFormCustomizeData(s), {
    headerLogoAssetKey: s.logoAssetKey.value,
    footerLogoAssetKey: s.includeFooter.value ? s.logoAssetKey.value : null,
  })
}

/** Token data for wizard previews: live form values, profile as fallback. */
export function wizardTokenData(s: WizardState): TokenResolutionData {
  const p = s.profile.value
  return {
    agency: {
      name: s.agencyName.value.trim() || p.agencyName,
      phone: trimmed(s.agencyPhone.value) ?? p.agencyContact.phone,
      url: trimmed(s.agencyUrl.value) ?? p.agencyContact.url,
      email: trimmed(s.agencyEmail.value) ?? p.agencyContact.email,
      linkedIn: trimmed(s.agencyLinkedIn.value) ?? p.agencyContact.linkedIn,
    },
    recruiter: {
      name: trimmed(s.recruiterName.value) ?? p.recruiter.name,
      phone: trimmed(s.recruiterPhone.value) ?? p.recruiter.phone,
      email: trimmed(s.recruiterEmail.value) ?? p.recruiter.email,
      linkedIn: trimmed(s.recruiterLinkedIn.value) ?? p.recruiter.linkedIn,
    },
  }
}

/**
 * Diffs the info-step form against the saved profiles: any non-empty
 * value that differs becomes a write-back intent ("the form IS the
 * profile"). Emptied fields are left untouched on the profile — removing
 * a line from your header shouldn't erase saved contact details.
 */
export function formWriteBackIntents(s: WizardState): WriteBackIntents {
  const intents = emptyWriteBacks()
  const saved = s.profile.value
  const diff = (raw: string, savedValue: string | undefined) => {
    const t = trimmed(raw)
    return t && t !== savedValue?.trim() ? t : undefined
  }
  const agency: Array<
    [keyof WriteBackIntents['agencyContact'], string, string | undefined]
  > = [
    ['phone', s.agencyPhone.value, saved.agencyContact.phone],
    ['url', s.agencyUrl.value, saved.agencyContact.url],
    ['email', s.agencyEmail.value, saved.agencyContact.email],
    ['linkedIn', s.agencyLinkedIn.value, saved.agencyContact.linkedIn],
  ]
  for (const [key, raw, savedValue] of agency) {
    const v = diff(raw, savedValue)
    if (v) intents.agencyContact[key] = v
  }
  const recruiter: Array<
    [keyof WriteBackIntents['recruiter'], string, string | undefined]
  > = [
    ['name', s.recruiterName.value, saved.recruiter.name],
    ['phone', s.recruiterPhone.value, saved.recruiter.phone],
    ['email', s.recruiterEmail.value, saved.recruiter.email],
    ['linkedIn', s.recruiterLinkedIn.value, saved.recruiter.linkedIn],
  ]
  for (const [key, raw, savedValue] of recruiter) {
    const v = diff(raw, savedValue)
    if (v) intents.recruiter[key] = v
  }
  return intents
}
